import {
  ConnectorMode,
  FontWeight,
  StrokeStyle,
} from '@blocksuite/affine-model';
import type { EditorHost } from '@blocksuite/block-std';
import { type EdgelessRootService, NoteDisplayMode, type RootService } from '@blocksuite/blocks';
import type { Doc } from '@blocksuite/store';
import type { EditorService } from '@pique/core/modules/editor';
import type { SerializedTranscriptionChunk } from '@pique/core/modules/properties/services/schema';
import type { TranscriptionService as PropertiesTranscriptionService } from '@pique/core/modules/properties/services/transcription-service';

// Prefer schema types from properties service
export type TranscriptionChunkRecord = SerializedTranscriptionChunk; // Use centralized type only
export interface TranscriptionStore { chunks: SerializedTranscriptionChunk[] }
// Typed accessor to properties transcription service for a host/doc
export function getPropertiesTranscriptionService(host: EditorHost): PropertiesTranscriptionService | null {
  try {
    if (!host?.doc) return null;
    const pageService = host.spec.getService<RootService>('affine:page');
    const svc = pageService?.transcriptionService as PropertiesTranscriptionService | undefined;
    return svc ?? null;
  } catch {
    return null;
  }
}



interface CardStackOptions {
  containerRect: DOMRect;
  startX: number;
  startY: number;
  stackSize: number;
  edgelessRootService: EdgelessRootService;
  doc: Doc;
}

export function getRandomPastelColor(): string {
  const hue = Math.floor(Math.random() * 80) + 260;
  const saturation = Math.floor(Math.random() * 20) + 60;
  const lightness = Math.floor(Math.random() * 15) + 45;
  const alpha = 0.9;
  return `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`;
}

export const createCardStack = ({
  containerRect,
  startX,
  startY,
  stackSize,
  edgelessRootService,
  doc,
}: CardStackOptions): string[] => {
  const cardIDs: string[] = [];
  const cardWidth = containerRect.width * 0.6;
  const cardHeight = 400;
  const stackOffset = 20;

  // Create a frame to contain all cards
  edgelessRootService.addBlock(
    'affine:frame',
    {
      title: new doc.Text('Hey, Quick Question!'),
      xywh: [startX - 20, startY - 20, cardWidth + 60, cardHeight].join(','),
    },
    edgelessRootService.surface
  );

  for (let i = 0; i < stackSize; i++) {
    const cardX = startX + i * stackOffset;
    const cardY = startY + i * stackOffset;

    const cardId = edgelessRootService.addBlock(
      'affine:note',
      {
        xywh: [cardX, cardY, cardWidth, cardHeight].join(','),
        background: getRandomPastelColor(),
        displayMode: NoteDisplayMode.EdgelessOnly,
        edgeless: {
          style: {
            borderRadius: 24,
            shadowType: '--affine-note-shadow-sticker',
            borderSize: 0,
          },
        },
        index: edgelessRootService.layer.generateIndex('affine:note'),
      },
      doc.root?.id
    );

    // Add title
    const titleId = edgelessRootService.addBlock(
      'affine:paragraph',
      {
        text: new doc.Text(`Quiz Card ${i + 1}`),
        type: 'h1',
      },
      cardId
    );

    edgelessRootService.updateElement(titleId, {
      style: {
        color: '--affine-palette-line-blue',
        fontSize: '24px',
        fontWeight: 'bold',
        textAlign: 'center',
      },
    });

    // Add question
    const questionId = edgelessRootService.addBlock(
      'affine:paragraph',
      {
        text: new doc.Text('What is the capital of France?'),
        type: 'text',
      },
      cardId
    );

    edgelessRootService.updateElement(questionId, {
      style: {
        fontSize: '22px',
        fontWeight: 'medium',
        marginTop: '20px',
        marginBottom: '20px',
      },
    });

    // Add options
    const options = ['London', 'Berlin', 'Paris', 'Madrid'];
    options.forEach((option, index) => {
      const optionId = edgelessRootService.addBlock(
        'affine:list',
        {
          text: new doc.Text(option),
          checked: index === 2,
          type: 'todo',
        },
        cardId
      );

      edgelessRootService.updateElement(optionId, {
        style: {
          fontSize: '18px',
          marginLeft: '20px',
        },
      });
    });

    // Add decorative elements
    const shapePositions = [{ x: cardWidth + 100, y: 60 }];
    shapePositions.forEach(() => {
      const isEven = i % 2 === 0;
      const shapeX = isEven
        ? cardX - 140 + Math.random() * 20
        : cardX + cardWidth + 70 + Math.random() * 20;

      const shapeId = edgelessRootService.addElement('shape', {
        shapeType: 'diamond',
        xywh: [shapeX, cardY + 60 + Math.random() * 160, 80, 80].join(','),
        strokeWidth: 2,
        index: edgelessRootService.layer.generateIndex('shape'),
        text: 'Hint!',
        fontSize: 14,
        fontWeight: FontWeight.SemiBold,
      });

      edgelessRootService.addElement('connector', {
        source: { id: cardId },
        target: { id: shapeId },
        strokeWidth: 3,
        strokeStyle: StrokeStyle.Dash,
        stroke: '--affine-brand-color',
        mode: ConnectorMode.Curve,
        index: edgelessRootService.layer.generateIndex('connector'),
      });
    });

    cardIDs.push(cardId);
  }

  edgelessRootService.addElement('group', {
    children: cardIDs,
    title: 'Quiz Card Group',
    index: edgelessRootService.layer.generateIndex('group'),
  });

  return cardIDs;
};

export async function initializeEdgelessPage(
  editorService: EditorService
): Promise<EdgelessRootService | null> {
  const editor = editorService.editor;
  const editorContainer = editor.editorContainer$.value;

  if (editorContainer?.host) {
    const host = editorContainer.host;
    const rootService = host.spec.getService('affine:page') as unknown as RootService | undefined;
    const edgelessRootService =
      await rootService?.getSidebarEdgelessRootService?.();
    return edgelessRootService || null;
  }
  return null;
}

export function addFullViewportNoteBlock(
  edgelessRootService: EdgelessRootService
): string {
  return edgelessRootService.addFullViewportBlock();
}

export async function addAudioAttachment(
  edgelessRootService: EdgelessRootService,
  doc: Doc,
  containerRect: DOMRect,
  audioBlob: Blob,
  noteId: string
): Promise<string> {
  const audioHeight = 300;
  const sourceId = await doc.blobSync.set(audioBlob);
  return edgelessRootService.addBlock(
    'affine:embed-pique-audio-attachment',
    {
      name: `Recording_${new Date().toISOString()}.webm`,
      size: audioBlob.size,
      type: audioBlob.type,
      sourceId,
      xywh: [0, 0, containerRect.width, audioHeight].join(','),
      isTranscribing: true,
      chunkDurationMs: 5000,
      importantMarks: [],
      confusedMarks: [],
      recordingStartTime: Date.now(),
    },
    noteId
  );
}

export async function addYoutubeAttachment(
  edgelessRootService: EdgelessRootService,
  youtubeUrl: string,
  noteId: string
): Promise<string> {
  return edgelessRootService.addBlock(
    'affine:embed-pique-youtube',
    {
      url: youtubeUrl,
    },
    noteId
  );
}


// Hacky utility to grab the current EditorHost from DOM if services are unavailable
export function getCurrentEditorHost(): EditorHost | null {
  if (typeof document === 'undefined') return null;
  const edgelessRoot = document.querySelector<HTMLElement & { host?: EditorHost }>('affine-edgeless-root');
  if (edgelessRoot?.host?.doc) return edgelessRoot.host;
  const maybeHost = document.querySelector('editor-host') as unknown as EditorHost | null;
  if (maybeHost?.doc) return maybeHost;
  return null;
}
